import * as Apollo from "@apollo/client";
import { pipe, TaskEither } from "fp";
import moment from "moment";

import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as ErrorMessage from "~/ErrorMessage";
import * as Log from "~/Log";
import { Remote as GenericRemote } from "~/Remote";
import { Request } from "~/Request";
import { Time } from "~/Time";
import { WebAPI } from "~/WebAPI";

/**
 * `annotationType` (UC/EC/ADC) is deliberately not sent — it isn't a confirmed 1:1
 * mapping of the old REST `exception_type` (YM/PU); see WebAPI/Revision's
 * `CreateRevisionSuggestionInput` comment and this MR's description.
 */
export const request = (client: Apollo.ApolloClient<unknown>) => ({
  driver,
  suggestion,
  timezone,
}: {
  readonly driver: Data.Reference.Reference;
  readonly suggestion: Omit<
    Log.Suggestion.Suggestion,
    "sources" | "event" | "driver"
  >;
  readonly timezone: Time.Zone;
}): Request.Fn<Log.Revision.Revision> => () =>
  pipe(
    TaskEither.fromUnsafe(() =>
      client.mutate<
        WebAPI.Revision.CreateRevisionSuggestionResult,
        WebAPI.Revision.CreateRevisionSuggestionVariables
      >({
        mutation: WebAPI.Revision.CreateRevisionSuggestionDocument,
        variables: {
          driverId: Number(driver.id),
          input: {
            status: Driver.Status.Actual.fromStatus(suggestion.status),

            location: suggestion.location ?? "",
            notes: suggestion.notes ?? "",

            startTime: moment(suggestion.interval.start.dateTime)
              .tz(timezone.zone, true)
              .toISOString(),
            endTime: moment(suggestion.interval.stop.dateTime)
              .tz(timezone.zone, true)
              .toISOString(),

            workOrderId: suggestion.workOrder?.id
              ? Number(suggestion.workOrder.id)
              : undefined,
            workCodeIds: suggestion.workCodes?.map(({ id }) => Number(id)),
            jobId: suggestion.job?.id ? Number(suggestion.job.id) : undefined,
          },
        },
      })
    ),
    TaskEither.mapLeft((error) => {
      console.error(error);
      return (
        WebAPI.ServiceError.fromMutationError(error) ??
        Error(
          `Something is wrong with your connection, ${ErrorMessage.Remedy.Retry.later()} or ${ErrorMessage.Remedy.contactSupport()}.`
        )
      );
    }),
    TaskEither.chain((result) =>
      result.data
        ? TaskEither.right(result.data.createRevisionSuggestion)
        : TaskEither.left(
            Error(
              `Something is wrong with our data, ${ErrorMessage.Remedy.contactSupport()}.`
            )
          )
    ),
    TaskEither.map(Log.Revision.Request.decodeFromGQL(driver))
  );

export namespace Update {
  export const use = () => GenericRemote.Mutation.use(request(WebAPI.useClient()));
}
